import { Fragment, type ReactNode } from 'react'

import { Icon } from '@iconify/react'
import { Button, Checkbox, Col, Radio, Row, Skeleton } from 'antd'
import { useFormikContext } from 'formik'

import { ErrorLabel } from 'common/components/ErrorLabel/ErrorLabel'
import { Input } from 'common/components/Input/Input'
import { InputNumber } from 'common/components/InputNumber/InputNumber'
import { Label } from 'common/components/Label/Label'
import { Select } from 'common/components/Select/Select'

import type {
  DynamicFieldValue,
  DynamicFieldValues,
  EmrCodeDynamicField,
} from 'features/Procedure/state/procedure-api.type'

import styles from './dynamic-fields.module.scss'
import {
  getBindingOptions,
  getColSpan,
  getDynamicFieldName,
  isRenderableField,
  NUMBER_FIELD_DISPLAY_DECIMALS,
  NUMBER_FIELD_STEP,
  toDecimalValue,
  toDynamicFieldRows,
  toIntegerValue,
} from './dynamic-fields.utils'
import type { FormValues } from './schema'

type Props = {
  disabled?: boolean
}

const SKELETON_PLACEHOLDER_KEYS = ['first', 'second', 'third']

const AUTO_COL_FLEX = '1 0 0%'

export const DynamicFieldsSkeleton = () => (
  <>
    {SKELETON_PLACEHOLDER_KEYS.map((key) => (
      <Col xs={8} key={key}>
        <Skeleton active title={false} paragraph={{ rows: 2, width: ['40%', '100%'] }} />
      </Col>
    ))}
  </>
)

type SetFieldErrors = Record<string, string | undefined>

export const DynamicFields = ({ disabled = false }: Props) => {
  const { values, errors, setFieldValue } = useFormikContext<FormValues>()

  const rows = toDynamicFieldRows(values.dynamic_fields)
    .map((row) => ({ ...row, elements: row.elements.filter(isRenderableField) }))
    .filter((row) => row.elements.length > 0)
  const storedSets = values.dynamic_field_values ?? []
  const valueSets: DynamicFieldValues[] = storedSets.length ? storedSets : [{}]
  const setErrors = (errors.dynamic_field_values ?? []) as Array<SetFieldErrors | undefined>

  if (!rows.length) {
    return null
  }

  const updateSets = (newSets: DynamicFieldValues[]) => {
    setFieldValue('dynamic_field_values', newSets)
  }

  const handleValueChange = (setIndex: number, name: string, newValue: DynamicFieldValue) => {
    updateSets(
      valueSets.map((set, index) => (index === setIndex ? { ...set, [name]: newValue } : set)),
    )
  }

  const handleAddSet = () => {
    updateSets([...valueSets, {}])
  }

  const handleRemoveSet = (setIndex: number) => {
    updateSets(valueSets.filter((_, index) => index !== setIndex))
  }

  const renderControl = (
    field: EmrCodeDynamicField,
    setIndex: number,
    fieldValues: DynamicFieldValues,
    fieldErrors: SetFieldErrors,
  ): ReactNode => {
    const name = getDynamicFieldName(field)
    const value = fieldValues[name]
    const error = fieldErrors[name]

    switch (field.type) {
      case 'integer':
        return (
          <>
            <Label label={field.label} required={field.required} />
            <InputNumber
              value={value as number | undefined}
              name={name}
              precision={0}
              formatter={(formatValue) => String(formatValue ?? '').replace(/\D/g, '')}
              parser={(text) => (text ?? '').replace(/\D/g, '')}
              onChange={(newValue) =>
                handleValueChange(
                  setIndex,
                  name,
                  newValue === null || newValue === undefined
                    ? undefined
                    : toIntegerValue(newValue),
                )
              }
              placeholder={field.label}
              disabled={disabled}
              status={error ? 'error' : ''}
            />
            <ErrorLabel error={error} />
          </>
        )
      case 'number':
        return (
          <>
            <Label label={field.label} required={field.required} />
            <InputNumber
              value={value as number | undefined}
              name={name}
              precision={NUMBER_FIELD_DISPLAY_DECIMALS}
              step={NUMBER_FIELD_STEP}
              incrementStep={NUMBER_FIELD_STEP}
              decrementStep={NUMBER_FIELD_STEP}
              onChange={(newValue) =>
                handleValueChange(
                  setIndex,
                  name,
                  newValue === null || newValue === undefined
                    ? undefined
                    : toDecimalValue(newValue),
                )
              }
              placeholder={field.label}
              disabled={disabled}
              status={error ? 'error' : ''}
            />
            <ErrorLabel error={error} />
          </>
        )
      case 'dropdown':
        return (
          <Select
            label={field.label}
            required={field.required}
            value={(value as string | undefined) ?? undefined}
            listOptions={getBindingOptions(field.bindings)}
            onChange={(newValue: string) => handleValueChange(setIndex, name, newValue)}
            placeholder={field.label}
            disabled={disabled}
            error={error}
            allowClear
          />
        )
      case 'multiselect':
        return (
          <Select
            label={field.label}
            required={field.required}
            mode='multiple'
            containerClassName={styles.multiSelect}
            value={(value as string[] | undefined) ?? []}
            listOptions={getBindingOptions(field.bindings)}
            onChange={(newValue: string[]) => handleValueChange(setIndex, name, newValue)}
            placeholder={field.label}
            disabled={disabled}
            error={error}
            allowClear
          />
        )
      case 'checkboxgroup':
        return (
          <>
            <Label label={field.label} required={field.required} />
            <Checkbox.Group
              className={styles.optionGroup}
              value={(value as string[] | undefined) ?? []}
              options={getBindingOptions(field.bindings)}
              onChange={(newValue) => handleValueChange(setIndex, name, newValue as string[])}
              disabled={disabled}
            />
            <ErrorLabel error={error} />
          </>
        )
      case 'radiogroup':
        return (
          <>
            <Label label={field.label} required={field.required} />
            <Radio.Group
              className={styles.optionGroup}
              value={(value as string | undefined) ?? null}
              options={getBindingOptions(field.bindings)}
              onChange={(event) => handleValueChange(setIndex, name, event.target.value)}
              disabled={disabled}
            />
            <ErrorLabel error={error} />
          </>
        )
      case 'string':
      default:
        return (
          <Input
            label={field.label}
            required={field.required}
            value={(value as string | undefined) ?? ''}
            name={name}
            onChange={(event) => handleValueChange(setIndex, name, event.target.value)}
            placeholder={field.label}
            disabled={disabled}
            error={error}
          />
        )
    }
  }

  return (
    <>
      {valueSets.map((fieldValues, setIndex) => {
        const fieldErrors = setErrors[setIndex] ?? {}
        return (
          // sets have no identity beyond their position in the form values
          <Fragment key={setIndex}>
            {setIndex > 0 && (
              <Col xs={24}>
                <div className={styles.setSeparator}>
                  <Button
                    danger
                    type='text'
                    size='small'
                    disabled={disabled}
                    onClick={() => handleRemoveSet(setIndex)}
                    aria-label={`Remove set ${setIndex + 1}`}
                    icon={<Icon icon='mdi:trash-can-outline' width={16} height={16} />}
                  />
                </div>
              </Col>
            )}
            {rows.map((row, rowIndex) => (
              <Col xs={24} key={`${setIndex}-${rowIndex}`}>
                <Row gutter={[16, 16]}>
                  {row.elements.map((field) => {
                    const span = getColSpan(field.col)
                    return (
                      <Col
                        key={getDynamicFieldName(field)}
                        {...(span !== undefined ? { xs: span } : { flex: AUTO_COL_FLEX })}>
                        {renderControl(field, setIndex, fieldValues, fieldErrors)}
                      </Col>
                    )
                  })}
                </Row>
              </Col>
            ))}
          </Fragment>
        )
      })}
      <Col xs={24}>
        <Button
          type='dashed'
          size='small'
          disabled={disabled}
          onClick={handleAddSet}
          aria-label='Add set'
          icon={<Icon icon='mdi:plus' width={16} height={16} />}>
          Add set
        </Button>
      </Col>
    </>
  )
}
